/**
 * Navigation vocabulary rendered by the sidebar: the groups, their items,
 * and the accounting regime each gated entry is declared for.
 *
 * `key` is typed against the core ModuleKey union, so an entry the
 * vocabulary does not know about fails to compile.
 */
import {
  Rocket,
  LayoutDashboard,
  FileText,
  Users,
  Package,
  Boxes,
  Warehouse,
  BarChart3,
  UserCog,
  Shield,
  ShieldAlert,
  ClipboardCheck,
  Settings,
  Plug,
  ShoppingCart,
  Calculator,
  BookOpen,
  NotebookPen,
  BookText,
  Scale,
  Link2,
  Landmark,
  HandCoins,
  CalendarClock,
  Percent,
  FileBarChart2,
  CalendarDays,
  Lock,
  Settings2,
  Truck,
  ReceiptText,
  Building2,
  TrendingDown,
  Layers,
  Wallet,
  Target,
  Banknote,
  Stamp,
  ShoppingBag,
  ClipboardList,
  Briefcase,
  UserPlus,
  TrendingUp,
  ListChecks,
  FolderKanban,
  GitBranch,
  CreditCard,
  FileUp,
  FileSignature,
  Repeat,
  BellRing,
  ScrollText,
  NotebookText,
  FileCheck,
  BookMarked,
  Ship,
  Webhook,
  Store,
  FileCode2,
  BookUser,
  Sigma,
  Paperclip,
  ArrowDownToLine,
  Server,
  FolderOpen,
} from 'lucide-react';
import type { ModuleKey, Regime } from '@efacture/core/sector';
import type { JurisdictionSurface } from '@/api/queries/jurisdiction';
import { SETTINGS_PERMISSIONS } from '@/pages/admin/settings/sections';

/**
 * Two polarities, because they answer differently while the regime is
 * unknown: `except` shows the entry, `only` hides it. A pharmacy
 * therefore never sees an NGO entry flash while the regime loads.
 */
import { REGISTERED_MODULE_UIS } from '../../modules';

export type RegimeAffinity =
  | { only: readonly Regime[] }
  | { except: readonly Regime[] };

export function regimeAllows(
  entry: { regimes?: RegimeAffinity },
  regime: Regime | null
): boolean {
  const affinity = entry.regimes;
  if (!affinity) return true;
  if ('only' in affinity) {
    return regime !== null && affinity.only.includes(regime);
  }
  return regime === null || !affinity.except.includes(regime);
}

export interface NavItem {
  key: ModuleKey;
  /** Accounting regimes this entry exists for. Absent means every regime. */
  regimes?: RegimeAffinity;
  /** Absent pour un item `heading` (sous-titre non cliquable). */
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  labelKey: string;
  /** Sous-titre de section à l'intérieur d'un groupe (non cliquable). */
  heading?: boolean;
  /** Excluded from the group grant: this item needs its own key in the
   *  resolved profile, granting the enclosing group is not enough. Carried
   *  by the items a capability pack owns, so the pack has something to add
   *  — its group is one every profile already holds. */
  packOnly?: boolean;
  /** Code de fonctionnalité de plan (constants/plans.ts). Masqué si le
   *  plan effectif du tenant ne l'inclut pas ; FAIL-OPEN quand l'état de
   *  facturation est inconnu (desktop embarqué, utilisateur sans
   *  billing:read). */
  planFeature?: string;
  permission?: string;
  /** Visible si l'utilisateur détient AU MOINS UNE de ces permissions
   *  (ex. Imports : clients/articles/fournisseurs/balance d'ouverture) */
  anyPermission?: string[];
  badge?: number;
  /** Actif uniquement sur correspondance exacte (liens index, ex. /accounting) */
  exact?: boolean;
  /** Hidden in the desktop (Electron) shell: operations a local instance
   *  cannot perform (e.g. the SaaS subscription, PI-SPI webhooks). */
  desktopHidden?: boolean;
  /** Hidden when the company's jurisdiction does not provide this surface
   *  (GET /settings/jurisdiction `surfaces`); shown while it is unknown. */
  surface?: JurisdictionSurface;
}

export interface NavGroup {
  key: ModuleKey;
  /** Accounting regimes this group exists for. Absent means every regime. */
  regimes?: RegimeAffinity;
  labelKey?: string;
  items: NavItem[];
  /** Section repliable (rendue avec un bouton d'en-tête + chevron) */
  collapsible?: boolean;
  /** Icône d'en-tête pour les sections repliables */
  icon?: React.ComponentType<{ className?: string }>;
  /** Permission requise pour afficher la section entière */
  permission?: string;
  /** Code de fonctionnalité de plan pour la section entière. */
  planFeature?: string;
}

const BASE_NAV_GROUPS: NavGroup[] = [
  {
    key: 'main',
    items: [
      { key: 'dashboard', href: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', permission: 'dashboard:view' },
    ],
  },
  // ── Ventes & Caisse — tout le cycle order-to-cash, Caisse en tête ──
  {
    key: 'ventes-caisse',
    labelKey: 'nav.ventesCaisse',
    icon: ShoppingCart,
    collapsible: true,
    items: [
      { key: 'pos', href: '/pos', icon: ShoppingCart, labelKey: 'nav.pos', permission: 'pos:access', planFeature: 'pos', packOnly: true, surface: 'POS' },
      { key: 'invoices', href: '/invoices', icon: FileText, labelKey: 'nav.invoices', permission: 'invoices:read', planFeature: 'invoices' },
      { key: 'verification', href: '/invoices/verification', icon: ClipboardCheck, labelKey: 'nav.verification', permission: 'invoices:verify', planFeature: 'invoices' },
      // ★ SIGNATURES MCF EN DOUTE — entrée CONDITIONNELLE, filtrée plus
      // bas sur `mcfEnDouteCount === 0`. Elle n'est PAS gardée par un
      // `planFeature` : une divergence de signature n'appartient à aucune
      // option commerciale, elle arrive à quiconque signe. Le double
      // garde-fou (permission ET existence d'une ligne) est ce qui rend
      // l'ajout invisible pour les dossiers sains — la « zéro churn »
      // exigée : 8 dossiers, 60 003 factures, aucune ligne de journal
      // antérieure au journal lui-même.
      { key: 'mcf-signatures-en-doute', href: '/invoices/signatures-en-doute', icon: ShieldAlert, labelKey: 'nav.mcfSignaturesEnDoute', permission: 'mcf:arbitrer-signature', surface: 'MCF' },
      { key: 'sales-quotes', href: '/ventes/devis', icon: FileSignature, labelKey: 'nav.sales.quotes', permission: 'quotes:read', planFeature: 'quotes' },
      { key: 'sales-delivery-notes', href: '/ventes/bons-livraison', icon: Truck, labelKey: 'nav.sales.deliveryNotes', permission: 'deliveries:read', planFeature: 'deliveries' },
      { key: 'sales-recurring', href: '/ventes/recurrentes', icon: Repeat, labelKey: 'nav.sales.recurring', permission: 'invoices:read', planFeature: 'invoices', surface: 'RECURRING_INVOICES' },
      { key: 'sales-reminders', href: '/ventes/relances', icon: BellRing, labelKey: 'nav.sales.reminders', permission: 'invoices:read', planFeature: 'invoices' },
    ],
  },
  // ── Clients & CRM — le client et son amont commercial ──
  {
    key: 'clients-crm',
    labelKey: 'nav.clientsCrm',
    icon: Users,
    collapsible: true,
    items: [
      { key: 'clients', href: '/clients', icon: Users, labelKey: 'nav.clients', permission: 'clients:read', planFeature: 'clients' },
      { key: 'crm-leads', href: '/crm/prospects', icon: UserPlus, labelKey: 'nav.crm.leads', permission: 'crm:read', planFeature: 'crm', packOnly: true },
      { key: 'crm-opportunities', href: '/crm/opportunites', icon: TrendingUp, labelKey: 'nav.crm.opportunities', permission: 'crm:read', planFeature: 'crm', packOnly: true },
      { key: 'crm-activities', href: '/crm/activites', icon: ListChecks, labelKey: 'nav.crm.activities', permission: 'crm:read', planFeature: 'crm', packOnly: true },
    ],
  },
  {
    key: 'catalogue',
    labelKey: 'nav.catalogue',
    icon: Package,
    collapsible: true,
    items: [
      { key: 'articles', href: '/articles', icon: Package, labelKey: 'nav.articles', permission: 'articles:read', planFeature: 'articles' },
      { key: 'stock', href: '/stock', icon: Boxes, labelKey: 'nav.stock', permission: 'stock:read', planFeature: 'stock', packOnly: true },
      // Under stock:adjust, like the page itself: a depot is created and stock
      // moved between depots by whoever may correct quantities.
      { key: 'depots', href: '/stock/depots', icon: Warehouse, labelKey: 'nav.depots', permission: 'stock:adjust', planFeature: 'stock', packOnly: true },
      // A counter holds `stock:inventory:count` and no `stock:read`: this is its way in.
      { key: 'inventories', href: '/stock/inventaires', icon: ClipboardList, labelKey: 'nav.inventaires', anyPermission: ['stock:inventory', 'stock:inventory:count'], planFeature: 'stock', packOnly: true },
    ],
  },
  // ── Achats & Fournisseurs — chaîne procure-to-pay réunifiée
  // (fournisseurs et factures fournisseurs étaient enfouis dans
  // Comptabilité, coupés des bons de commande/importations) ──
  {
    key: 'achats',
    labelKey: 'nav.achats',
    icon: ShoppingBag,
    collapsible: true,
    items: [
      { key: 'suppliers', href: '/accounting/fournisseurs', icon: Truck, labelKey: 'nav.accounting.suppliers', permission: 'suppliers:read', planFeature: 'suppliers' },
      { key: 'purchase-orders', href: '/achats/bons-de-commande', icon: ClipboardList, labelKey: 'nav.purchasing.purchaseOrders', permission: 'purchases:read', planFeature: 'purchases' },
      { key: 'supplier-invoices', href: '/accounting/factures-fournisseurs', icon: ReceiptText, labelKey: 'nav.accounting.supplierInvoices', permission: 'suppliers:read', planFeature: 'suppliers' },
      { key: 'import-costs', href: '/achats/importations', icon: Ship, labelKey: 'nav.purchasing.importCosts', permission: 'purchases:read', planFeature: 'purchases', packOnly: true },
    ],
  },
  // ── Comptabilité (régime NORMAL) — sous-titrée par familles ; masquée
  // entièrement quand le tenant est au régime SMT (exclusif) ──
  {
    key: 'accounting',
    regimes: { except: ['SMT'] },
    labelKey: 'nav.accounting',
    icon: Calculator,
    permission: 'accounting:read',
    planFeature: 'accounting',
    collapsible: true,
    items: [
      { key: 'accounting-dashboard', href: '/accounting', icon: LayoutDashboard, labelKey: 'nav.accounting.dashboard', permission: 'accounting:read', exact: true },
      { key: 'h-saisie', heading: true, labelKey: 'nav.accounting.h.saisie' },
      { key: 'accounting-entries', href: '/accounting/ecritures', icon: NotebookPen, labelKey: 'nav.accounting.entries', permission: 'accounting:read' },
      { key: 'accounting-auxiliaries', href: '/accounting/tiers', icon: BookUser, labelKey: 'nav.accounting.auxiliaries', permission: 'accounting:read' },
      { key: 'accounting-lettering', href: '/accounting/lettrage', icon: Link2, labelKey: 'nav.accounting.lettering', permission: 'accounting:read' },
      { key: 'accounting-reconciliation', href: '/accounting/rapprochement', icon: Landmark, labelKey: 'nav.accounting.reconciliation', permission: 'accounting:read' },
      { key: 'accounting-supporting-documents', href: '/accounting/pieces-justificatives', icon: Paperclip, labelKey: 'nav.accounting.supportingDocuments', permission: 'accounting:read' },
      { key: 'h-etats', heading: true, labelKey: 'nav.accounting.h.etats' },
      { key: 'accounting-journal-report', href: '/accounting/journal', icon: NotebookText, labelKey: 'nav.accounting.journalReport', permission: 'accounting:read' },
      { key: 'accounting-general-ledger', href: '/accounting/grand-livre', icon: BookText, labelKey: 'nav.accounting.generalLedger', permission: 'accounting:read' },
      { key: 'accounting-trial-balance', href: '/accounting/balance', icon: Scale, labelKey: 'nav.accounting.trialBalance', permission: 'accounting:read' },
      // Même page que la balance, ouverte sur l'axe analytique (?analytique=1).
      // Retour UAT 2026-08-07 : la vue analytique n'existait que sous la forme
      // d'un filtre « Axe analytique » DANS la balance générale, sans aucune
      // entrée de menu portant son nom — un utilisateur qui cherchait « balance
      // analytique » tombait sur « Axes analytiques », l'écran de CONFIGURATION
      // des axes, et en concluait que le rapport n'existait pas.
      { key: 'accounting-analytic-balance', href: '/accounting/balance-analytique', icon: GitBranch, labelKey: 'nav.accounting.analyticBalance', permission: 'accounting:read' },
      { key: 'accounting-auxiliary-balance', href: '/accounting/balance-auxiliaire', icon: Sigma, labelKey: 'nav.accounting.auxiliaryBalance', permission: 'accounting:read' },
      { key: 'accounting-aged-balance', href: '/accounting/balance-agee', icon: CalendarClock, labelKey: 'nav.accounting.agedBalance', permission: 'accounting:read' },
      { key: 'accounting-statements', href: '/accounting/etats', icon: FileBarChart2, labelKey: 'nav.accounting.statements', permission: 'accounting:read', regimes: { except: ['SYCEBNL'] } },
      { key: 'h-immos', heading: true, labelKey: 'nav.accounting.h.immos' },
      { key: 'accounting-assets', href: '/accounting/immobilisations', icon: Building2, labelKey: 'nav.accounting.assets', permission: 'accounting:read' },
      { key: 'accounting-depreciations', href: '/accounting/dotations', icon: TrendingDown, labelKey: 'nav.accounting.depreciations', permission: 'accounting:read' },
      { key: 'h-pilotage', heading: true, labelKey: 'nav.accounting.h.pilotage' },
      { key: 'accounting-budgets', href: '/accounting/budgets', icon: Wallet, labelKey: 'nav.accounting.budgets', permission: 'accounting:read', exact: true },
      { key: 'accounting-budget-vs-actual', href: '/accounting/budgets/realise', icon: Target, labelKey: 'nav.accounting.budgetVsActual', permission: 'accounting:read' },
      { key: 'accounting-cost-centers', href: '/accounting/centres-de-couts', icon: Layers, labelKey: 'nav.accounting.costCenters', permission: 'accounting:read' },
      { key: 'h-referentiel', heading: true, labelKey: 'nav.accounting.h.referentiel' },
      { key: 'accounting-analytic-axes', href: '/accounting/axes-analytiques', icon: GitBranch, labelKey: 'nav.accounting.analyticAxes', permission: 'accounting:read' },
      { key: 'accounting-chart', href: '/accounting/plan-comptable', icon: BookOpen, labelKey: 'nav.accounting.chartOfAccounts', permission: 'accounting:read' },
      { key: 'accounting-periods', href: '/accounting/periodes', icon: CalendarDays, labelKey: 'nav.accounting.periods', permission: 'accounting:read' },
      { key: 'accounting-closing', href: '/accounting/cloture', icon: Lock, labelKey: 'nav.accounting.closing', permission: 'accounting:close' },
      { key: 'accounting-exchange-rates', href: '/accounting/taux-de-change', icon: Banknote, labelKey: 'nav.accounting.exchangeRates', permission: 'accounting:settings' },
      { key: 'accounting-settings', href: '/accounting/parametres', icon: Settings2, labelKey: 'nav.accounting.settings', permission: 'accounting:settings' },
    ],
  },
  // ── Comptabilité simplifiée (régime SMT) — remplace la section
  // Comptabilité quand le tenant est au SMT (jamais les deux) ──
  {
    key: 'smt',
    regimes: { only: ['SMT'] },
    items: [
      { key: 'accounting-smt', href: '/compta-simplifiee', icon: BookMarked, labelKey: 'nav.accounting.smt', permission: 'accounting:read', planFeature: 'accounting' },
      // Second door to /accounting/parametres, where the regime is
      // chosen. The entry of the same name lives in the `accounting`
      // group, which is `except: ['SMT']` and therefore filtered out
      // whole under SMT — items inside it are never considered. Without
      // this one an SMT tenant has no navigation route back to any other
      // regime. It carries no regime affinity of its own: the group's
      // `only: ['SMT']` already scopes it.
      { key: 'accounting-settings-smt', href: '/accounting/parametres', icon: Settings2, labelKey: 'nav.accounting.settingsSmt', permission: 'accounting:settings', planFeature: 'accounting' },
    ],
  },
  // ── Financements (verticale ONG palier A, Task 4) — référentiel
  // bailleurs/accords/catégories/composantes/activités/sources. GROUPE
  // À PART (jamais un enfant d'Achats) : il sert aussi bien un tenant
  // SYCEBNL qu'une entreprise SYSCOHADA qui exécute un projet bailleur.
  // Gaté par la fonctionnalité de plan `funding` ET la permission
  // `funding:read` (que `accounting:*` NE couvre PAS).
  //
  // `only` is safe for this group and for Rapports bailleur below, unlike
  // Budget further down: every shipped role holding `funding:read` or
  // `reporting:read` (ACCOUNTANT, RESPONSABLE_FINANCIER_PROJET) also holds
  // `accounting:read`, so the regime resolves for anyone who could see
  // these entries. Budget's operators cannot read it at all, which is why
  // it declares no affinity.
  {
    key: 'funding',
    regimes: { only: ['SYCEBNL'] },
    items: [
      { key: 'funding', href: '/financements', icon: HandCoins, labelKey: 'nav.funding', permission: 'funding:read', planFeature: 'funding' },
    ],
  },
  // ── Rapports bailleur (verticale ONG palier C, Task 6) ── Groupe À
  // PART du budget : restituer à un bailleur n'est pas exécuter une
  // dépense, et `budget:*` n'atteint pas `reporting:*`. Gaté par la
  // permission, par la fonctionnalité de plan `funding` ET — depuis la
  // correction du cloisonnement ONG — par le régime SYCEBNL.
  //
  // ⚠ La note qui figurait ici (« aucun rôle NORMAL ne porte
  // `reporting:read`, l'entrée leur reste invisible ») était FAUSSE pour
  // les administrateurs : `*` satisfait `reporting:read`, et les sept
  // dossiers en production sont hérités du plan SUITE, qui inclut
  // `funding`. Les deux gardes tombaient : d'où le filtre de régime.
  {
    key: 'reporting',
    regimes: { only: ['SYCEBNL'] },
    items: [
      {
        key: 'reporting-donor',
        href: '/rapports-bailleur',
        icon: HandCoins,
        labelKey: 'nav.reporting.donor',
        permission: 'reporting:read',
        planFeature: 'funding',
      },
    ],
  },
  // ── Budget — le moteur d'engagement (verticale ONG palier B1) ──
  // Crédit ouvert → engagé → liquidé (RGCP art. 91). Gaté par la
  // fonctionnalité de plan `budget` ET la permission `budget:read`, que
  // `accounting:*` NE couvre PAS : l'exécution budgétaire relève de
  // l'ordonnateur. ⚠ Ces deux attributs ne masquent que le MENU — le
  // gating serveur (`URL_FEATURE_MAP`) et `budget:read` sur chaque route
  // sont ce qui ferme réellement la porte.
  //
  // ★ DÉLIBÉRÉMENT PAS filtré par le régime comptable, contrairement à
  // `funding` et `reporting` ci-dessus. Deux raisons, dans cet ordre :
  //
  //  1. LA DONNÉE DE RÉGIME EST HORS D'ATTEINTE DE SES OPÉRATEURS.
  //     ORDONNATEUR, CONTROLEUR_FINANCIER, COMPTABLE_PUBLIC et
  //     AUTORITE_BUDGETAIRE portent `budget:read` SANS `accounting:read`
  //     (constants/roles.ts). Or `GET /accounting/settings/accounting-
  //     system` — qui sert `accountingSystem` ET `publicExecution` —
  //     exige `accounting:read` ; routes/budget/index.ts le dit mot pour
  //     mot : « que l'ordonnateur n'a pas ». Pour ces quatre rôles les
  //     deux champs sont nuls POUR TOUJOURS, pas le temps d'un
  //     chargement : tout filtre de régime masquerait le module à ses
  //     seuls opérateurs, définitivement.
  //  2. Le moteur ne dépend NI de SYCEBNL NI du référentiel bailleurs :
  //     une enveloppe n'exige qu'UN axe, et un préfixe de compte
  //     SYSCOHADA suffit (`NOMENCLATURE_REQUISE`, envelope.service.ts).
  //     services/budget/public-execution.ts déclare l'orthogonalité au
  //     régime en toutes lettres.
  {
    key: 'budget',
    labelKey: 'nav.budget',
    icon: Wallet,
    collapsible: true,
    permission: 'budget:read',
    planFeature: 'budget',
    items: [
      { key: 'budget-execution', href: '/budget/execution', icon: Layers, labelKey: 'nav.budget.execution', permission: 'budget:read', planFeature: 'budget' },
      { key: 'budget-envelopes', href: '/budget/enveloppes', icon: Wallet, labelKey: 'nav.budget.envelopes', permission: 'budget:read', planFeature: 'budget' },
      { key: 'budget-commitments', href: '/budget/engagements', icon: FileSignature, labelKey: 'nav.budget.commitments', permission: 'budget:read', planFeature: 'budget' },
      // ★ Mode public (palier B2). Gatées sur `budget:read` comme leurs
      // voisines : les permissions séparées (`budget:visa`,
      // `budget:passer-outre`, `budget:pay`) gardent les BOUTONS, pas les
      // écrans — un ordonnateur doit pouvoir suivre où en est son dossier
      // au visa, et un contrôleur voir ce qui a été réglé.
      { key: 'budget-visas', href: '/budget/visas', icon: Stamp, labelKey: 'nav.budget.visas', permission: 'budget:read', planFeature: 'budget' },
      { key: 'budget-ordonnancement', href: '/budget/ordonnancement', icon: Banknote, labelKey: 'nav.budget.ordonnancement', permission: 'budget:read', planFeature: 'budget' },
    ],
  },
  // ── Fiscalité — toute la surface DGI en une maison ──
  {
    key: 'fiscalite',
    labelKey: 'nav.fiscalite',
    icon: ScrollText,
    collapsible: true,
    items: [
      { key: 'declarations-list', href: '/declarations', icon: FileCheck, labelKey: 'nav.declarations.list', permission: 'declarations:read', planFeature: 'declarations', exact: true, surface: 'DECLARATIONS' },
      { key: 'declarations-calendar', href: '/declarations/calendrier', icon: CalendarClock, labelKey: 'nav.declarations.calendar', permission: 'declarations:read', planFeature: 'declarations', surface: 'DECLARATIONS' },
      { key: 'accounting-vat', href: '/accounting/tva', icon: Percent, labelKey: 'nav.accounting.vat', permission: 'accounting:read', planFeature: 'accounting', surface: 'DECLARATIONS' },
      { key: 'accounting-liasse-esintax', href: '/accounting/liasse-esintax', icon: FileCode2, labelKey: 'nav.accounting.liasseEsintax', permission: 'accounting:read', planFeature: 'accounting', regimes: { except: ['SYCEBNL'] }, surface: 'DECLARATIONS' },
    ],
  },
  // No group-level permission: the employee screens (`hr:read`) and payroll
  // (`payroll:read`) are held by different roles — ACCOUNTANT holds payroll
  // only. Each item carries its own gate, and composeNav drops the group
  // when none of them is visible.
  {
    key: 'hr',
    labelKey: 'nav.hr',
    icon: Briefcase,
    collapsible: true,
    items: [
      { key: 'hr-employees', href: '/rh/employes', icon: Users, labelKey: 'nav.hr.employees', permission: 'hr:read', planFeature: 'hr' },
      { key: 'hr-leaves', href: '/rh/conges', icon: CalendarDays, labelKey: 'nav.hr.leaves', permission: 'hr:read', planFeature: 'hr' },
      { key: 'hr-payroll', href: '/rh/paie', icon: Banknote, labelKey: 'nav.hr.payroll', permission: 'payroll:read', planFeature: 'payroll', exact: true, surface: 'PAYROLL' },
      { key: 'hr-payroll-parameters', href: '/rh/paie/parametres', icon: Settings2, labelKey: 'nav.hr.payrollParameters', permission: 'payroll:read', planFeature: 'payroll', surface: 'PAYROLL' },
    ],
  },
  {
    key: 'projects',
    labelKey: 'nav.projects',
    icon: FolderKanban,
    permission: 'projects:read',
    planFeature: 'projects',
    collapsible: true,
    items: [
      { key: 'projects-list', href: '/projets', icon: FolderKanban, labelKey: 'nav.projects.list', permission: 'projects:read' },
    ],
  },
  // ── Reports: the X, Z and A reports (SFE spec §2.12, §4, §5) belong to
  // every offer that invoices; the tabbed hub /rapports (Rapports,
  // Analyses, Personnalisés) to the offers holding « reports ». ──
  {
    key: 'reports-hub',
    items: [
      { key: 'reports-fiscal', href: '/rapports-fiscaux', icon: FileBarChart2, labelKey: 'nav.rapportsFiscaux', permission: 'reports:daily', planFeature: 'invoices', surface: 'FISCAL_REPORTS' },
      { key: 'reports', href: '/rapports', icon: BarChart3, labelKey: 'nav.rapportsAnalyses', permission: 'dashboard:view', planFeature: 'reports' },
    ],
  },
  {
    key: 'cabinet',
    items: [
      { key: 'cabinet', href: '/cabinet', icon: Briefcase, labelKey: 'nav.cabinet', permission: 'cabinet:access', exact: true },
      { key: 'cabinet-dossiers', href: '/cabinet/dossiers', icon: FolderOpen, labelKey: 'nav.cabinetDossiers', permission: 'cabinet:access' },
    ],
  },
  // ── Administration — sous-titrée par sujets ──
  {
    key: 'admin',
    labelKey: 'nav.administration',
    icon: Settings,
    collapsible: true,
    items: [
      { key: 'h-acces', heading: true, labelKey: 'nav.admin.h.acces' },
      { key: 'users', href: '/admin/users', icon: UserCog, labelKey: 'nav.users', permission: 'users:read' },
      { key: 'roles', href: '/admin/roles', icon: Shield, labelKey: 'nav.roles', permission: 'roles:read' },
      { key: 'audit', href: '/audit', icon: ScrollText, labelKey: 'nav.audit', permission: 'audit:view' },
      { key: 'h-structure', heading: true, labelKey: 'nav.admin.h.structure' },
      { key: 'establishments', href: '/admin/establishments', icon: Store, labelKey: 'nav.establishments', permission: 'establishments:read', packOnly: true },
      { key: 'settings', href: '/settings', icon: Settings, labelKey: 'nav.settings', anyPermission: [...SETTINGS_PERMISSIONS] },
      { key: 'h-integrations', heading: true, labelKey: 'nav.admin.h.integrations' },
      { key: 'erp-connection', href: '/admin/erp-connection', icon: Plug, labelKey: 'nav.erpConnection', permission: 'tenant:settings' },
      { key: 'webhooks', href: '/parametres/webhooks', icon: Webhook, labelKey: 'nav.webhooks', permission: 'webhooks:read', planFeature: 'webhooks' },
      {
        key: 'pispi-events',
        href: '/admin/pispi-events',
        icon: ArrowDownToLine,
        labelKey: 'nav.pispiEvents',
        permission: 'pispi:configure',
        desktopHidden: true,
      },
      { key: 'onboarding', href: '/bienvenue', icon: Rocket, labelKey: 'nav.onboarding', permission: 'tenant:settings' },
      {
        key: 'imports',
        href: '/parametres/imports',
        icon: FileUp,
        labelKey: 'nav.imports',
        anyPermission: [
          'clients:create',
          'articles:create',
          'suppliers:write',
          'crm:write',
          'hr:write',
          'accounting:write',
          'accounting:settings',
        ],
      },
      { key: 'h-saas', heading: true, labelKey: 'nav.admin.h.saas' },
      { key: 'billing', href: '/parametres/abonnement', icon: CreditCard, labelKey: 'nav.billing', permission: 'billing:read', desktopHidden: true },
      {
        key: 'node-health',
        href: '/admin/nodes',
        icon: Server,
        labelKey: 'nav.nodeHealth',
        permission: 'tenant:settings',
        desktopHidden: true,
        surface: 'NODE_ENROLMENT',
      },
      { key: 'cabinet-settings', href: '/settings/cabinet', icon: Briefcase, labelKey: 'nav.cabinetSettings', permission: 'tenant:settings' },
    ],
  },
];

/**
 * Core's own sidebar plus what the modules contribute. A module either
 * brings a whole group or adds items into one that already exists; a
 * vertical usually does both.
 *
 * Groups are copied before items are pushed into them, so a module never
 * mutates the base literal.
 */
export const navigationGroups: NavGroup[] = (() => {
  const composed: NavGroup[] = BASE_NAV_GROUPS.map((g) => ({ ...g, items: [...g.items] }));
  const byKey = new Map(composed.map((g) => [g.key, g]));

  for (const ui of REGISTERED_MODULE_UIS) {
    for (const contribution of ui.nav) {
      if ('group' in contribution) {
        const group: NavGroup = { ...contribution.group, items: [...contribution.group.items] };
        composed.push(group);
        byKey.set(group.key, group);
        continue;
      }
      const target = byKey.get(contribution.intoGroup);
      if (!target) {
        throw new Error(
          `Le module ${ui.code} ajoute des entrées dans le groupe inconnu « ${contribution.intoGroup} »`
        );
      }
      if (contribution.after === undefined) {
        target.items.push(...contribution.items);
        continue;
      }
      const at = target.items.findIndex((i) => i.key === contribution.after);
      if (at === -1) {
        throw new Error(
          `Le module ${ui.code} ancre des entrées après « ${contribution.after} », absent du groupe « ${contribution.intoGroup} »`
        );
      }
      target.items.splice(at + 1, 0, ...contribution.items);
    }
  }

  return composed;
})();
